import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import { CALENDAR_VIEW, SOURCE_STATUS } from '../labels'
import {
  addMonths,
  dayOf,
  daysInMonth,
  isWeekend,
  isoOfDay,
  longDate,
  monthTitle,
  periodOf,
  shortDate,
  weekdayInitial,
} from '../lib/format'
import type { CycleView, SourceCell, Sprint } from '../lib/types'
import {
  ACCENT,
  BAND,
  FONT_MONO,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  LINE_SOFT,
  PAPER,
  STATUS_COLOR,
  TINT,
  serifNumber,
} from '../theme'

interface Props {
  cells: SourceCell[]
  cycles: CycleView[]
  sprints: Sprint[]
  period: string
  bounds: { min: string; max: string }
  today: string
  onPeriodChange: (period: string) => void
  onSelect: (cell: SourceCell) => void
}

/** Ancho de la columna de nombres; lo comparten el eje y las tres secciones. */
const NAME_W = 168

/** Estados en los que la fecha comprometida ya se venció sin entrega. */
const OVERDUE_STATUSES = new Set(['delayed', 'action_required', 'check_failed', 'expected_today'])
const ROW_H = 30

/**
 * El mes, día por día.
 *
 * Las otras dos vistas responden "¿cómo venimos?". Ésta responde "¿qué pasa
 * esta semana?": la fecha exacta de cada entrega, dónde cae la ventana de cada
 * ciclo y dónde va el sprint, todo sobre el mismo eje de días para que se vean
 * los choques —una entrega que aterriza el mismo día que cierra un sprint, un
 * deadline que cae en sábado— que ninguna lista puesta en columnas deja ver.
 *
 * Los fines de semana van sombreados porque los deadlines se planean en días
 * hábiles: una barra que termina en sábado, en la práctica, termina el lunes.
 */
export function MonthView({
  cells,
  cycles,
  sprints,
  period,
  bounds,
  today,
  onPeriodChange,
  onSelect,
}: Props) {
  const days = daysInMonth(period)
  const col = 100 / days
  const center = (d: number) => (d - 0.5) * col
  const start = (d: number) => (d - 1) * col

  const monthStart = `${period}-01`
  const monthEnd = isoOfDay(period, days)
  const todayDay = periodOf(today) === period ? dayOf(today) : null

  /** Recorta un intervalo al mes visible; marca los bordes que se salen. */
  const clamp = (from: string, to: string) => {
    if (to < monthStart || from > monthEnd) return null
    return {
      from: from < monthStart ? 1 : dayOf(from),
      to: to > monthEnd ? days : dayOf(to),
      openStart: from < monthStart,
      openEnd: to > monthEnd,
    }
  }

  const deliveries = cells
    .filter(
      (c) =>
        (c.expected_date && periodOf(c.expected_date) === period) ||
        (c.actual_date && periodOf(c.actual_date) === period),
    )
    .sort((a, b) => (a.expected_date ?? a.actual_date ?? '').localeCompare(b.expected_date ?? b.actual_date ?? ''))

  const monthCycles = cycles
    .map((c) => ({ cycle: c, bar: clamp(c.window_start, c.deadline) }))
    .filter((x) => x.bar !== null)

  const monthSprints = sprints
    .filter((s) => s.start_date && s.end_date)
    .map((s) => ({ sprint: s, bar: clamp(s.start_date!, s.end_date!) }))
    .filter((x) => x.bar !== null)

  const empty = deliveries.length === 0 && monthCycles.length === 0 && monthSprints.length === 0

  return (
    <Box sx={{ px: 3, py: 3 }}>
      <MonthNav period={period} bounds={bounds} onChange={onPeriodChange} />

      <Box sx={{ overflowX: 'auto', mt: 2.5 }}>
        <Box sx={{ minWidth: 880 }}>
          {/* Eje de días: inicial de la semana arriba, número abajo. */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: 1 }}>
            <Box sx={{ width: NAME_W, flexShrink: 0 }} />
            <Box sx={{ position: 'relative', flex: 1, height: 34 }}>
              {Array.from({ length: days }, (_, i) => i + 1).map((d) => {
                const wknd = isWeekend(period, d)
                const isToday = d === todayDay
                return (
                  <Box
                    key={d}
                    sx={{
                      position: 'absolute',
                      left: `${center(d)}%`,
                      transform: 'translateX(-50%)',
                      textAlign: 'center',
                      fontFamily: FONT_MONO,
                      lineHeight: 1.4,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: '0.5rem',
                        letterSpacing: '0.06em',
                        color: wknd ? '#C4B9AC' : INK_FAINT,
                      }}
                    >
                      {weekdayInitial(period, d)}
                    </Box>
                    <Box
                      sx={{
                        fontSize: '0.6875rem',
                        fontWeight: isToday ? 600 : 400,
                        color: isToday ? PAPER : wknd ? '#B5A99B' : INK_SOFT,
                        bgcolor: isToday ? ACCENT : 'transparent',
                        borderRadius: 999,
                        minWidth: 19,
                        px: 0.4,
                      }}
                    >
                      {d}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </Box>

          {empty ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: FONT_MONO, fontSize: '0.75rem', color: INK_FAINT }}>
                {CALENDAR_VIEW.emptyMonth}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              {/* Capa de fondo: fines de semana y la línea de hoy, detrás de todo. */}
              <Box
                sx={{
                  position: 'absolute',
                  left: NAME_W,
                  right: 0,
                  top: 0,
                  bottom: 0,
                  pointerEvents: 'none',
                }}
              >
                {Array.from({ length: days }, (_, i) => i + 1)
                  .filter((d) => isWeekend(period, d))
                  .map((d) => (
                    <Box
                      key={d}
                      sx={{
                        position: 'absolute',
                        left: `${start(d)}%`,
                        width: `${col}%`,
                        top: 0,
                        bottom: 0,
                        bgcolor: TINT.sand,
                        opacity: 0.7,
                      }}
                    />
                  ))}

                {todayDay !== null && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${center(todayDay)}%`,
                      top: -8,
                      bottom: -4,
                      width: 2,
                      ml: '-1px',
                      bgcolor: ACCENT,
                      opacity: 0.9,
                      borderRadius: 999,
                    }}
                  />
                )}
              </Box>

              <Section title={CALENDAR_VIEW.deliveries} count={deliveries.length}>
                {deliveries.map((cell) => (
                  <DeliveryRow
                    key={`${cell.source_id}-${cell.period}`}
                    cell={cell}
                    period={period}
                    center={center}
                    todayDay={todayDay}
                    onSelect={onSelect}
                  />
                ))}
              </Section>

              {monthCycles.length > 0 && (
                <Section title={CALENDAR_VIEW.cycles} count={monthCycles.length}>
                  {monthCycles.map(({ cycle, bar }) => (
                    <CycleRow
                      key={`${cycle.cycle_id}-${cycle.data_period}`}
                      cycle={cycle}
                      bar={bar!}
                      period={period}
                      start={start}
                      center={center}
                      col={col}
                    />
                  ))}
                </Section>
              )}

              {monthSprints.length > 0 && (
                <Section title={CALENDAR_VIEW.sprints} count={monthSprints.length} last>
                  {monthSprints.map(({ sprint, bar }) => (
                    <SprintRow
                      key={sprint.id}
                      sprint={sprint}
                      bar={bar!}
                      today={today}
                      start={start}
                      col={col}
                    />
                  ))}
                </Section>
              )}
            </Box>
          )}

          {!empty && <MonthLegend />}
        </Box>
      </Box>
    </Box>
  )
}

/**
 * Leyenda mínima: sólo las tres convenciones que esta vista inventa y que no
 * se deducen mirando. El resto del código de color ya vive en la leyenda de
 * estados de las otras vistas.
 */
function MonthLegend() {
  const item = (mark: React.ReactNode, label: string) => (
    <Stack direction="row" spacing={0.75} alignItems="center" key={label}>
      {mark}
      <Box
        component="span"
        sx={{
          fontFamily: FONT_MONO,
          fontSize: '0.5625rem',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: INK_FAINT,
        }}
      >
        {label}
      </Box>
    </Stack>
  )

  const pill = (filled: boolean) => (
    <Box
      sx={{
        width: 26,
        height: 15,
        borderRadius: 999,
        bgcolor: filled ? INK_SOFT : PAPER,
        border: `1.5px solid ${INK_SOFT}`,
      }}
    />
  )

  return (
    <Stack
      direction="row"
      spacing={2.5}
      flexWrap="wrap"
      useFlexGap
      sx={{ mt: 3, pt: 2, borderTop: `1px solid ${LINE_SOFT}` }}
    >
      {item(pill(true), CALENDAR_VIEW.legendArrived)}
      {item(pill(false), CALENDAR_VIEW.legendExpected)}
      {item(
        <Box sx={{ width: 16, height: 15, bgcolor: TINT.sand, border: `1px solid ${LINE}` }} />,
        CALENDAR_VIEW.legendWeekend,
      )}
      {item(
        <Box sx={{ width: 2.5, height: 15, bgcolor: ACCENT, borderRadius: 999 }} />,
        CALENDAR_VIEW.legendToday,
      )}
    </Stack>
  )
}

function MonthNav({
  period,
  bounds,
  onChange,
}: {
  period: string
  bounds: { min: string; max: string }
  onChange: (p: string) => void
}) {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <IconButton
        size="small"
        aria-label={CALENDAR_VIEW.prevMonth}
        disabled={period <= bounds.min}
        onClick={() => onChange(addMonths(period, -1))}
      >
        <ChevronLeftIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Typography sx={{ ...serifNumber, fontSize: '1.75rem', minWidth: 210 }}>
        {monthTitle(period)}
      </Typography>
      <IconButton
        size="small"
        aria-label={CALENDAR_VIEW.nextMonth}
        disabled={period >= bounds.max}
        onClick={() => onChange(addMonths(period, 1))}
      >
        <ChevronRightIcon sx={{ fontSize: 20 }} />
      </IconButton>
    </Stack>
  )
}

function Section({
  title,
  count,
  last,
  children,
}: {
  title: string
  count: number
  last?: boolean
  children: React.ReactNode
}) {
  if (count === 0) return null
  return (
    <Box sx={{ pb: last ? 0 : 2, mb: last ? 0 : 2, borderBottom: last ? 'none' : `1px solid ${LINE_SOFT}` }}>
      <Box
        sx={{
          width: NAME_W,
          fontFamily: FONT_MONO,
          fontSize: '0.5625rem',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: INK_FAINT,
          mb: 1,
        }}
      >
        {title}
      </Box>
      <Stack spacing={0.25}>{children}</Stack>
    </Box>
  )
}

function Row({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: ROW_H }}>
      <Box sx={{ width: NAME_W, flexShrink: 0, pr: 2 }}>{label}</Box>
      <Box sx={{ position: 'relative', flex: 1, height: ROW_H }}>{children}</Box>
    </Box>
  )
}

/**
 * Una entrega en el mes.
 *
 * La cápsula lleva el día escrito, no sólo la posición: preguntar "¿cuándo cae
 * Nielsen?" no debería obligar a subir la vista hasta el eje y contar columnas.
 * Rellena = ya llegó; con borde = comprometida y todavía no.
 */
function DeliveryRow({
  cell,
  period,
  center,
  todayDay,
  onSelect,
}: {
  cell: SourceCell
  period: string
  center: (d: number) => number
  todayDay: number | null
  onSelect: (c: SourceCell) => void
}) {
  const color = STATUS_COLOR[cell.status] ?? '#CFC5B7'
  const expectedDay =
    cell.expected_date && periodOf(cell.expected_date) === period ? dayOf(cell.expected_date) : null
  const actualDay =
    cell.actual_date && periodOf(cell.actual_date) === period ? dayOf(cell.actual_date) : null

  // El rastro sólo se dibuja para lo que de verdad se pasó de fecha. Una
  // fuente "próxima" con fecha vieja es un dato mal capturado, no un retraso:
  // pintarle la línea roja sería inventar una alarma.
  const overdue = OVERDUE_STATUSES.has(cell.status)
  const drifting =
    overdue && expectedDay !== null && actualDay === null && todayDay !== null && todayDay > expectedDay

  const tooltip = [
    `${cell.source_name} · ${cell.period_label}`,
    `${CALENDAR_VIEW.expected}: ${longDate(cell.expected_date)}`,
    cell.actual_date ? `${CALENDAR_VIEW.arrived}: ${longDate(cell.actual_date)}` : null,
    SOURCE_STATUS[cell.status],
    cell.detail,
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <Row
      label={
        <Tooltip title={cell.source_name}>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {cell.source_name}
          </Typography>
        </Tooltip>
      }
    >
      {/* Rastro del retraso: de la fecha comprometida hasta hoy. */}
      {drifting && (
        <Box
          sx={{
            position: 'absolute',
            left: `${center(expectedDay!)}%`,
            width: `${center(todayDay!) - center(expectedDay!)}%`,
            top: '50%',
            height: 2,
            mt: '-1px',
            backgroundImage: `repeating-linear-gradient(to right, ${color} 0 4px, transparent 4px 8px)`,
          }}
        />
      )}

      {/* Movimiento entre lo comprometido y lo real. */}
      {expectedDay !== null && actualDay !== null && actualDay !== expectedDay && (
        <Box
          sx={{
            position: 'absolute',
            left: `${center(Math.min(expectedDay, actualDay))}%`,
            width: `${Math.abs(center(actualDay) - center(expectedDay))}%`,
            top: '50%',
            height: 1,
            bgcolor: LINE,
          }}
        />
      )}

      {expectedDay !== null && (actualDay === null || actualDay !== expectedDay) && (
        <DayPill
          day={expectedDay}
          color={color}
          center={center}
          tooltip={tooltip}
          onClick={() => onSelect(cell)}
          hollow
        />
      )}

      {actualDay !== null && (
        <DayPill
          day={actualDay}
          color={color}
          center={center}
          tooltip={tooltip}
          onClick={() => onSelect(cell)}
        />
      )}
    </Row>
  )
}

/** Cápsula con el número de día. Rellena = hecho; hueca = comprometido. */
function DayPill({
  day,
  color,
  center,
  tooltip,
  hollow,
  onClick,
}: {
  day: number
  color: string
  center: (d: number) => number
  tooltip: string
  hollow?: boolean
  onClick: () => void
}) {
  return (
    <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{tooltip}</Box>}>
      <Box
        onClick={onClick}
        sx={{
          position: 'absolute',
          left: `${center(day)}%`,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 0.6,
          height: 22,
          pl: 0.7,
          pr: 0.9,
          borderRadius: 999,
          cursor: 'pointer',
          bgcolor: hollow ? PAPER : color,
          border: `1.5px solid ${color}`,
          color: hollow ? color : PAPER,
          fontFamily: FONT_MONO,
          fontSize: '0.625rem',
          fontWeight: 600,
          transition: 'transform 160ms cubic-bezier(0.2,0.8,0.2,1)',
          '&:hover': { transform: 'translate(-50%, -50%) scale(1.12)' },
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            bgcolor: hollow ? color : PAPER,
            flexShrink: 0,
          }}
        />
        {day}
      </Box>
    </Tooltip>
  )
}

interface Bar {
  from: number
  to: number
  openStart: boolean
  openEnd: boolean
}

/** La ventana del ciclo dentro del mes, con muescas en cada fin de etapa. */
function CycleRow({
  cycle,
  bar,
  period,
  start,
  center,
  col,
}: {
  cycle: CycleView
  bar: Bar
  period: string
  start: (d: number) => number
  center: (d: number) => number
  col: number
}) {
  const deadlineDay = periodOf(cycle.deadline) === period ? dayOf(cycle.deadline) : null

  const ticks = cycle.stages
    .map((s) => s.actual_finish ?? s.projected_finish)
    .filter((iso) => periodOf(iso) === period)
    .map(dayOf)

  return (
    <Row
      label={
        <Tooltip title={cycle.cycle_name}>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {cycle.cycle_name}
          </Typography>
        </Tooltip>
      }
    >
      <Tooltip
        title={`${cycle.cycle_name}\n${shortDate(cycle.window_start)} → ${shortDate(cycle.deadline)}\n${CALENDAR_VIEW.sprintProgress}: ${Math.round(cycle.progress * 100)}%`}
      >
        <Box
          sx={{
            position: 'absolute',
            left: `${start(bar.from)}%`,
            width: `${(bar.to - bar.from + 1) * col}%`,
            top: 4,
            height: 22,
            bgcolor: `${cycle.color}26`,
            border: `1px solid ${cycle.color}66`,
            // Borde recto donde el intervalo sigue fuera del mes.
            borderTopLeftRadius: bar.openStart ? 2 : 999,
            borderBottomLeftRadius: bar.openStart ? 2 : 999,
            borderTopRightRadius: bar.openEnd ? 2 : 999,
            borderBottomRightRadius: bar.openEnd ? 2 : 999,
            display: 'flex',
            alignItems: 'center',
            px: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: FONT_MONO,
              fontSize: '0.5625rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: cycle.color,
              whiteSpace: 'nowrap',
            }}
          >
            {Math.round(cycle.progress * 100)}%
          </Box>
        </Box>
      </Tooltip>

      {ticks.map((d, i) => (
        <Box
          key={i}
          sx={{
            position: 'absolute',
            left: `${center(d)}%`,
            top: 6,
            height: 18,
            width: 1.5,
            ml: '-0.75px',
            bgcolor: cycle.color,
            opacity: 0.75,
          }}
        />
      ))}

      {deadlineDay !== null && (
        <Tooltip title={`${CALENDAR_VIEW.deadlineMark}: ${longDate(cycle.deadline)}`}>
          <Box
            sx={{
              position: 'absolute',
              left: `${center(deadlineDay)}%`,
              top: 1,
              height: 28,
              width: 2.5,
              ml: '-1.25px',
              bgcolor: INK,
              borderRadius: 999,
              cursor: 'help',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -3,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 7,
                height: 7,
                borderRadius: '50%',
                bgcolor: INK,
              },
            }}
          />
        </Tooltip>
      )}
    </Row>
  )
}

/** El sprint como barra con su avance rellenado por dentro. */
function SprintRow({
  sprint,
  bar,
  today,
  start,
  col,
}: {
  sprint: Sprint
  bar: Bar
  today: string
  start: (d: number) => number
  col: number
}) {
  const active = Boolean(
    sprint.start_date && sprint.end_date && sprint.start_date <= today && today <= sprint.end_date,
  )

  return (
    <Row
      label={
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: active ? 600 : 400,
            color: active ? INK : INK_SOFT,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {sprint.name}
        </Typography>
      }
    >
      <Tooltip
        title={`${sprint.label}\n${CALENDAR_VIEW.sprintProgress}: ${Math.round(sprint.progress * 100)}% · ${sprint.task_count} tasks`}
      >
        <Box
          sx={{
            position: 'absolute',
            left: `${start(bar.from)}%`,
            width: `${(bar.to - bar.from + 1) * col}%`,
            top: 5,
            height: 20,
            bgcolor: BAND,
            border: `1px solid ${active ? INK : LINE}`,
            borderTopLeftRadius: bar.openStart ? 2 : 999,
            borderBottomLeftRadius: bar.openStart ? 2 : 999,
            borderTopRightRadius: bar.openEnd ? 2 : 999,
            borderBottomRightRadius: bar.openEnd ? 2 : 999,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              width: `${sprint.progress * 100}%`,
              bgcolor: active ? INK : '#A99E90',
            }}
          />
          <Box
            component="span"
            sx={{
              position: 'relative',
              pl: 1,
              fontFamily: FONT_MONO,
              fontSize: '0.5625rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: sprint.progress > 0.12 ? PAPER : INK_SOFT,
              whiteSpace: 'nowrap',
            }}
          >
            {Math.round(sprint.progress * 100)}%
          </Box>
        </Box>
      </Tooltip>
    </Row>
  )
}
